import { newId } from './client.ts';
import { toDate } from './dates.ts';
import type { Activity, ActivityType, ChecklistItem, Game, InventoryItem } from './types.ts';

/**
 * Check-list automatique avant chaque réunion (fonction « Préparer ma réunion »).
 * Les items sont dérivés du type d'activité, du matériel des jeux associés et de
 * l'état de préparation réel (lieu manquant, aucun responsable désigné…), pour
 * que la liste reste utile plutôt que générique.
 */

export const BASE_CHECKLIST_BY_TYPE: Record<ActivityType, string[]> = {
  reunion_bala: [
    'Message parents',
    'Est-ce que le jeu est fait ?',
    'Répartir les rôles entre animateurs',
    'Aller chercher le goûter pour les enfants',
    'Préparer le matériel pour le jeu',
  ],
  reunion_surprise: [
    'Message parents',
    'Est-ce que le jeu est fait ?',
    'Répartir les rôles entre animateurs',
    'Aller chercher le goûter pour les enfants',
    'Préparer le matériel pour le jeu',
  ],
  reunion_staff: [
    'Préparer l’ordre du jour',
    'Relire le compte-rendu de la réunion précédente',
    'Faire le point sur les tâches en cours',
    'Point finances et intendance',
    'Évaluer les réunions passées et préparer le calendrier',
  ],
  reunion_parents: [],
  activite_staff: [],
  activite_unite: [],
  hike: [],
  camp: [],
};

const BASE_BY_TYPE = BASE_CHECKLIST_BY_TYPE;

/** Crée la check-list initiale par défaut pour un type d'activité. */
export function createDefaultChecklist(type: ActivityType): ChecklistItem[] {
  return (BASE_CHECKLIST_BY_TYPE[type] ?? []).map((label, index) => ({
    id: newId('chk'),
    label,
    done: false,
    assigneeId: null,
    auto: true,
    order: index,
  }));
}

export interface ChecklistSuggestion {
  label: string;
  /** Pourquoi l'item est proposé — affiché en aide contextuelle. */
  reason: string;
  /** Item bloquant : la réunion n'est pas prête tant qu'il n'est pas traité. */
  blocking: boolean;
}

/**
 * Analyse une activité et renvoie les items manquants. Les items déjà présents
 * dans la check-list (même libellé) ne sont pas proposés une seconde fois.
 */
export function suggestChecklist(
  activity: Pick<
    Activity,
    'type' | 'checklist' | 'placeId' | 'locationText' | 'responsibleIds' | 'gameIds' | 'materialForFamilies' | 'startAt'
  >,
  _games: Game[] = [],
  _inventory: Pick<InventoryItem, 'id' | 'name'>[] = [],
): ChecklistSuggestion[] {
  const existing = new Set(activity.checklist.map((item) => item.label.toLowerCase().trim()));
  const suggestions: ChecklistSuggestion[] = [];

  const add = (label: string, reason: string, blocking = false) => {
    if (existing.has(label.toLowerCase().trim())) return;
    suggestions.push({ label, reason, blocking });
  };

  for (const label of BASE_CHECKLIST_BY_TYPE[activity.type] ?? []) {
    add(label, `Étape requise pour une ${activity.type.replace('_', ' ')}`);
  }

  return suggestions;
}

/** Convertit des suggestions en items de check-list persistables. */
export function suggestionsToItems(
  suggestions: ChecklistSuggestion[],
  startOrder: number,
): ChecklistItem[] {
  return suggestions.map((suggestion, index) => ({
    id: newId('chk'),
    label: suggestion.label,
    done: false,
    assigneeId: null,
    auto: true,
    order: startOrder + index,
  }));
}

export interface ReadinessReport {
  /** Score de préparation de 0 à 100. */
  score: number;
  doneCount: number;
  totalCount: number;
  blockers: string[];
  ready: boolean;
}

/**
 * Indicateur « ma réunion est-elle prête ? ». Le score combine l'avancement de la
 * check-list et l'absence de points bloquants, ces derniers plafonnant le score
 * à 80 tant qu'ils ne sont pas levés.
 */
export function computeReadiness(
  activity: Pick<
    Activity,
    'type' | 'checklist' | 'placeId' | 'locationText' | 'responsibleIds' | 'gameIds' | 'materialForFamilies' | 'startAt'
  >,
  games: Game[] = [],
  // Le matériel des jeux vit dans l'inventaire : sans lui, on ne connaîtrait
  // que des identifiants, et la ligne dirait « rassembler inv_a3f… ».
  inventory: Pick<InventoryItem, 'id' | 'name'>[] = [],
): ReadinessReport {
  const totalCount = activity.checklist.length;
  const doneCount = activity.checklist.filter((item) => item.done).length;
  const blockers = suggestChecklist(activity, games, inventory)
    .filter((suggestion) => suggestion.blocking)
    .map((suggestion) => suggestion.reason);

  const progress = totalCount > 0 ? (doneCount / totalCount) * 100 : blockers.length === 0 ? 100 : 0;
  const score = blockers.length > 0 ? Math.min(80, Math.round(progress)) : Math.round(progress);

  return {
    score,
    doneCount,
    totalCount,
    blockers,
    ready: blockers.length === 0 && (totalCount === 0 || doneCount === totalCount),
  };
}
